import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axiosInstance from '../../api/axiosInstance';
import { getApiErrorMessage } from '../../utils/apiErrors';
import swal from '../../utils/swal';
import Loader from '../../components/loading/Loader';
import './Servicios.css';

const FORM_VACIO = {
  especialidad: { tipo: 'especialidad', nombre: '', duracionTurnoEnMins: '', costoConsulta: '' },
  practica:     { tipo: 'practica', codigo: '', nombre: '', duracionTurnoEnMins: '', costoConsulta: '' },
};

function construirPayload(formData) {
  const duracion = Number(formData.duracionTurnoEnMins);
  const costo = Number(formData.costoConsulta);

  if (!Number.isInteger(duracion) || duracion < 1) {
    return { error: 'La duración debe ser un número entero mayor a 0.' };
  }
  if (!Number.isFinite(costo) || costo <= 0) {
    return { error: 'El costo debe ser mayor a 0.' };
  }

  const payload = {
    tipo: formData.tipo,
    nombre: formData.nombre.trim(),
    duracionTurnoEnMins: duracion,
    costoConsulta: costo,
  };

  if (formData.tipo === 'practica') {
    payload.codigo = formData.codigo.trim();
  }

  return { payload };
}

function Servicios() {
  const { user } = useAuth();
  const medicoId = user?.medicoId;

  const [especialidades, setEspecialidades] = useState([]);
  const [practicas, setPracticas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(FORM_VACIO.especialidad);
  const [guardando, setGuardando] = useState(false);
  const [accionEnCurso, setAccionEnCurso] = useState(null);

  const cargarDatos = useCallback(async () => {
    if (!medicoId) return;
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(`/medicos/${medicoId}`);
      setEspecialidades(data.especialidades ?? []);
      setPracticas(data.practicas ?? []);
    } catch (err) {
      const mensaje = getApiErrorMessage(
        err,
        'No se pudieron cargar los servicios. Intentá de nuevo.'
      );
      await swal.fire({ icon: 'error', title: 'Error', text: mensaje, confirmButtonColor: '#db1414' });
    } finally {
      setLoading(false);
    }
  }, [medicoId]);

  useEffect(() => { cargarDatos(); }, [cargarDatos]);

  function abrirCrear(tipo) {
    setEditingId(null);
    setFormData(FORM_VACIO[tipo]);
    setFormOpen(true);
  }

  function abrirEditar(servicio, tipo) {
    setEditingId(servicio._id);
    setFormData({
      tipo,
      nombre: servicio.nombre,
      duracionTurnoEnMins: servicio.duracionTurnoEnMins,
      costoConsulta: servicio.costoConsulta,
      ...(tipo === 'practica' && { codigo: servicio.codigo }),
    });
    setFormOpen(true);
  }

  function cerrarForm() {
    setFormOpen(false);
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }

  async function handleGuardar(e) {
    e.preventDefault();

    const accion = editingId ? 'actualizar' : 'agregar';
    const tipo = formData.tipo === 'especialidad' ? 'especialidad' : 'práctica';
    const confirm = await swal.fire({
      title: `¿${accion === 'agregar' ? 'Agregar' : 'Actualizar'} ${tipo}?`,
      text: `Se ${accion === 'agregar' ? 'agregará' : 'actualizará'} el servicio en tu perfil.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
    });
    if (!confirm.isConfirmed) return;

    const { payload, error: errorValidacion } = construirPayload(formData);
    if (errorValidacion) {
      await swal.fire({
        icon: 'error',
        title: 'Datos inválidos',
        text: errorValidacion,
        confirmButtonColor: '#db1414',
      });
      return;
    }

    setGuardando(true);
    try {
      if (editingId) {
        await axiosInstance.patch(`/medicos/${medicoId}/servicios/${editingId}`, payload);
      } else {
        await axiosInstance.post(`/medicos/${medicoId}/servicios`, payload);
      }
      cerrarForm();
      await cargarDatos();
      await swal.fire({
        icon: 'success',
        title: 'Servicio guardado',
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (err) {
      const mensaje = getApiErrorMessage(
        err,
        'No se pudo guardar el servicio. Intentá de nuevo.'
      );
      await swal.fire({ icon: 'error', title: 'Error', text: mensaje, confirmButtonColor: '#db1414' });
    } finally {
      setGuardando(false);
    }
  }

  async function handleEliminar(servicioId, tipo) {
    const confirm = await swal.fire({
      title: '¿Eliminar servicio?',
      text: 'Esta acción no se puede deshacer.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Eliminar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#db1414',
    });
    if (!confirm.isConfirmed) return;

    setAccionEnCurso(servicioId);
    try {
      await axiosInstance.delete(`/medicos/${medicoId}/servicios/${servicioId}?tipo=${tipo}`);
      await cargarDatos();
    } catch (err) {
      const mensaje = getApiErrorMessage(
        err,
        'No se pudo eliminar el servicio. Intentá de nuevo.'
      );
      await swal.fire({ icon: 'error', title: 'Error', text: mensaje, confirmButtonColor: '#db1414' });
    } finally {
      setAccionEnCurso(null);
    }
  }

  if (!medicoId) {
    return (
      <p className="servicios__mensaje">
        No se encontró el perfil de médico. Cerrá sesión e ingresá nuevamente.
      </p>
    );
  }

  if (loading) return <Loader mensaje="Cargando servicios..." />;

  return (
    <div className="servicios">
      <section className="servicios__section">
        <header className="servicios__section-header">
          <span>ESPECIALIDADES</span>
          <button className="servicios__btn-agregar" onClick={() => abrirCrear('especialidad')}>
            + Agregar
          </button>
        </header>
        <div className="servicios__grid">
          {especialidades.length === 0 && (
            <p className="servicios__vacio">No tenés especialidades registradas.</p>
          )}
          {especialidades.map(esp => (
            <article key={esp._id} className="servicios__card">
              <p className="servicios__card-nombre">{esp.nombre}</p>
              <p className="servicios__card-detalle">Duración: {esp.duracionTurnoEnMins} min</p>
              <p className="servicios__card-detalle">Costo: ${esp.costoConsulta}</p>
              <div className="servicios__card-acciones">
                <button
                  className="servicios__btn servicios__btn--editar"
                  onClick={() => abrirEditar(esp, 'especialidad')}
                  disabled={accionEnCurso === esp._id}
                >
                  Editar
                </button>
                <button
                  className="servicios__btn servicios__btn--danger"
                  onClick={() => handleEliminar(esp._id, 'especialidad')}
                  disabled={accionEnCurso === esp._id}
                >
                  {accionEnCurso === esp._id ? <Loader inline /> : 'Eliminar'}
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="servicios__section">
        <header className="servicios__section-header">
          <span>PRÁCTICAS</span>
          <button className="servicios__btn-agregar" onClick={() => abrirCrear('practica')}>
            + Agregar
          </button>
        </header>
        <div className="servicios__grid">
          {practicas.length === 0 && (
            <p className="servicios__vacio">No tenés prácticas registradas.</p>
          )}
          {practicas.map(prac => (
            <article key={prac._id} className="servicios__card">
              <p className="servicios__card-nombre">{prac.nombre}</p>
              <p className="servicios__card-detalle">Código: {prac.codigo}</p>
              <p className="servicios__card-detalle">Duración: {prac.duracionTurnoEnMins} min</p>
              <p className="servicios__card-detalle">Costo: ${prac.costoConsulta}</p>
              <div className="servicios__card-acciones">
                <button
                  className="servicios__btn servicios__btn--editar"
                  onClick={() => abrirEditar(prac, 'practica')}
                  disabled={accionEnCurso === prac._id}
                >
                  Editar
                </button>
                <button
                  className="servicios__btn servicios__btn--danger"
                  onClick={() => handleEliminar(prac._id, 'practica')}
                  disabled={accionEnCurso === prac._id}
                >
                  {accionEnCurso === prac._id ? <Loader inline /> : 'Eliminar'}
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>

      {formOpen && (
        <div className="servicios__modal-backdrop" onClick={cerrarForm}>
          <div className="servicios__modal" onClick={e => e.stopPropagation()}>
            <h2 className="servicios__modal-titulo">
              {editingId ? 'Editar' : 'Agregar'}{' '}
              {formData.tipo === 'especialidad' ? 'especialidad' : 'práctica'}
            </h2>
            <form className="servicios__form" onSubmit={handleGuardar}>
              {formData.tipo === 'practica' && (
                <label className="servicios__form-label">
                  Código
                  <input
                    className="servicios__form-input"
                    name="codigo"
                    value={formData.codigo}
                    onChange={handleChange}
                    required
                  />
                </label>
              )}
              <label className="servicios__form-label">
                Nombre
                <input
                  className="servicios__form-input"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  required
                />
              </label>
              <label className="servicios__form-label">
                Duración del turno (min)
                <input
                  className="servicios__form-input"
                  name="duracionTurnoEnMins"
                  type="number"
                  min="1"
                  step="1"
                  value={formData.duracionTurnoEnMins}
                  onChange={handleChange}
                  required
                />
              </label>
              <label className="servicios__form-label">
                Costo de consulta ($)
                <input
                  className="servicios__form-input"
                  name="costoConsulta"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={formData.costoConsulta}
                  onChange={handleChange}
                  required
                />
              </label>
              <div className="servicios__form-acciones">
                <button
                  type="button"
                  className="servicios__btn servicios__btn--cancelar"
                  onClick={cerrarForm}
                  disabled={guardando}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="servicios__btn servicios__btn--confirmar"
                  disabled={guardando}
                >
                  {guardando ? <Loader inline /> : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Servicios;
